import React from 'react';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { css } from '@emotion/react';
import { OnwardSwitch } from '@/styles/blocks';
import { carrierKey } from '../panel/utils';

// A saved value stays listed even when it's missing from the options (a carrier without the role, a
// warehouse no longer in the carrier's locations), so the select still shows what the row holds.
const withSaved = (options, savedId, idKey, fallback) =>
    savedId && !options.some((o) => o[idKey] === savedId) ? [...options, fallback(savedId)] : options;

// One carrier's route, top to bottom: its Final Mile carrier (fixed here; remove the card and add
// another carrier to change it), the Middle Mile leg, then the receiving warehouse. Direct means no
// Middle Mile leg, so it clears and greys out the Middle Mile carrier. A select with nothing to offer
// and no saved value is disabled and says so.
const CarrierMappingRow = ({ mapping, name, carriersForLeg, allCarriers = [], onChange, disabled = false }) => {
    const savedCarrier = (id) =>
        allCarriers.find((c) => c.client_id === id) || { client_id: id, business_name: 'Unknown carrier' };
    const carrierOptions = carriersForLeg('carrier_id');
    const warehouseCarrierOptions = withSaved(
        carriersForLeg('warehouse_carrier_id'),
        mapping.warehouse_carrier_id,
        'client_id',
        savedCarrier
    );
    const mmCarrierOptions = withSaved(
        carriersForLeg('middle_mile_carrier_id'),
        mapping.middle_mile_carrier_id,
        'client_id',
        savedCarrier
    );
    const noMmCarriers = mmCarrierOptions.length === 0;

    // The receiving warehouse is one of the WH carrier's locations, else the Final Mile carrier's.
    const warehouseLocations = withSaved(
        (
            warehouseCarrierOptions.find((c) => c.client_id === (mapping.warehouse_carrier_id || mapping.carrier_id)) ||
            carrierOptions.find((c) => c.client_id === mapping.carrier_id)
        )?.locations || [],
        mapping.warehouse_id,
        'location_id',
        (id) =>
            allCarriers.flatMap((c) => c.locations || []).find((l) => l.location_id === id) || {
                location_id: id,
                location_name: 'Unknown warehouse',
            }
    );
    const noWarehouseCarriers = warehouseCarrierOptions.length === 0;
    const noWarehouses = warehouseLocations.length === 0;

    return (
        <Grid
            container
            direction="column"
            css={css`
                flex-wrap: nowrap;
                gap: 16px;
            `}
        >
            <TextField
                fullWidth
                variant="outlined"
                size="small"
                id={`final-mile-${carrierKey(mapping)}`}
                label="Final Mile"
                value={name || ''}
                InputProps={{ readOnly: true }}
            />
            <Grid
                container
                direction="column"
                css={css`
                    flex-wrap: nowrap;
                    gap: 4px;
                `}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    select
                    id={`mm-carrier-select-${carrierKey(mapping)}`}
                    label="Middle Mile"
                    value={mapping.middle_mile_carrier_id || ''}
                    onChange={(e) => onChange({ ...mapping, middle_mile_carrier_id: e.target.value })}
                    disabled={disabled || !!mapping.is_direct || noMmCarriers}
                    helperText={noMmCarriers ? 'No middle mile carriers available' : null}
                >
                    {mmCarrierOptions.map((c) => (
                        <MenuItem key={c.client_id} value={c.client_id}>
                            {c.business_name}
                        </MenuItem>
                    ))}
                </TextField>
                <OnwardSwitch
                    label="Direct (no middle mile)"
                    checked={!!mapping.is_direct}
                    onChange={(_, checked) =>
                        onChange({
                            ...mapping,
                            is_direct: checked,
                            ...(checked ? { middle_mile_carrier_id: null } : {}),
                        })
                    }
                    disabled={disabled}
                />
            </Grid>
            <TextField
                fullWidth
                variant="outlined"
                size="small"
                select
                id={`wh-carrier-select-${carrierKey(mapping)}`}
                label="WH Carrier"
                value={mapping.warehouse_carrier_id || ''}
                onChange={(e) => onChange({ ...mapping, warehouse_carrier_id: e.target.value, warehouse_id: null })}
                disabled={disabled || noWarehouseCarriers}
                helperText={noWarehouseCarriers ? 'No WH carriers available' : null}
            >
                {warehouseCarrierOptions.map((c) => (
                    <MenuItem key={c.client_id} value={c.client_id}>
                        {c.business_name}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                fullWidth
                variant="outlined"
                size="small"
                select
                id={`warehouse-select-${carrierKey(mapping)}`}
                label="Receiving WH"
                value={mapping.warehouse_id || ''}
                onChange={(e) => onChange({ ...mapping, warehouse_id: e.target.value })}
                disabled={disabled || noWarehouses}
                helperText={noWarehouses ? 'No Receiving WH available' : null}
            >
                {warehouseLocations.map((location, i) => (
                    <MenuItem key={location.location_id} value={location.location_id}>
                        {location.location_name || `Warehouse ${i + 1}`}
                    </MenuItem>
                ))}
            </TextField>
        </Grid>
    );
};

export default CarrierMappingRow;
